import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaWallet, FaChartLine, FaUsers, FaMoneyBillWave, FaCoins, FaTrophy, FaPlus, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TopupModal from '../components/TopupModal';
import { topupUser, toggleInvestorStatus } from '../services/userService';
import './UserDetails.css';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  useEffect(() => {
    // Get user data from location state (passed from Users component)
    if (location.state && location.state.user) {
      console.log('Usrer Data', location.state.user);
      setUserDetails(location.state.user);
    } else {
      // If no state, redirect back to users list
      navigate('/users');
    }
  }, [location, navigate]);

  const handleTopup = async (amount) => {
    try {
      const response = await topupUser(userDetails.id, amount);

      if (response.status) {
        // Update the local user details with new balance
        setUserDetails(prev => ({
          ...prev,
          available_income: prev.available_income + amount
        }));

        toast.success(`Successfully added $${amount.toLocaleString()} to user account`);
        setShowTopupModal(false);
      } else {
        throw new Error(response.message || 'Failed to process topup');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process topup');
      throw error;
    }
  };

  const handleInvestorToggle = async () => {
    if (isTogglingStatus) return;

    const currentStatus = userDetails.investor;
    const newStatus = currentStatus === 'enable' ? 'disable' : 'enable';

    setIsTogglingStatus(true);

    try {
      const response = await toggleInvestorStatus(userDetails.id, newStatus);

      if (response.status) {
        // Update the local user details with new investor status
        setUserDetails(prev => ({
          ...prev,
          investor: newStatus
        }));

        toast.success(`Investor ${newStatus === 'enable' ? 'enabled' : 'disabled'} successfully`);
      } else {
        throw new Error(response.message || 'Failed to toggle investor status');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to toggle investor status');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        Loading user details...
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="user-details-error">
        User not found
      </div>
    );
  }

  return (
    <div className="user-details-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-top">
            <button
              className="back-button"
              onClick={() => navigate('/users')}
            >
              <FaArrowLeft /> Back to Users
            </button>
            <div className="action-buttons">
              <button
                className={`investor-toggle-button ${userDetails.investor === 'enable' ? 'active' : 'inactive'}`}
                onClick={handleInvestorToggle}
                disabled={isTogglingStatus}
              >
                {isTogglingStatus ? (
                  'Processing...'
                ) : (
                  <>
                    {userDetails.investor === 'enable' ? (
                      <>
                        <FaUserTimes /> Disable Investor
                      </>
                    ) : (
                      <>
                        <FaUserCheck /> Enable Investor
                      </>
                    )}
                  </>
                )}
              </button>
              <button
                className="topup-button"
                onClick={() => setShowTopupModal(true)}
              >
                <FaPlus /> Topup Account
              </button>
            </div>
          </div>
          <div className="header-info">
            <h1>User Details</h1>
            <p>ID: #{userDetails.id} | Wallet: {userDetails.wallet}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card gradient-purple">
          <div className="stat-icon">
            <FaWallet />
          </div>
          <div className="stat-details">
            <h3>Total Deposit</h3>
            <p className="stat-value">${Number(userDetails.deposit).toLocaleString()}</p>
          </div>
        </div>

        <div className="stat-card gradient-green">
          <div className="stat-icon">
            <FaMoneyBillWave />
          </div>
          <div className="stat-details">
            <h3>Total Income</h3>
            <p className="stat-value">${Number(userDetails.total_income).toLocaleString()}</p>
          </div>
        </div>

        <div className="stat-card gradient-blue">
          <div className="stat-icon">
            <FaCoins />
          </div>
          <div className="stat-details">
            <h3>Available Balance</h3>
            <p className="stat-value">${Number(userDetails.available_income).toLocaleString()}</p>
          </div>
        </div>

        <div className="stat-card gradient-orange">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-details">
            <h3>Total Team</h3>
            <p className="stat-value">{userDetails.total_team}</p>
          </div>
        </div>
      </div>

      {/* Details Sections */}
      <div className="details-container">
        {/* Basic Information */}
        <div className="detail-card">
          <div className="card-header">
            <h2><FaWallet /> Basic Information</h2>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Wallet Address</label>
                <span className="wallet-address">{userDetails.wallet}</span>
              </div>
              <div className="info-item">
                <label>Sponsor Wallet</label>
                <span className="wallet-address">{userDetails.sponsor_wallet}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`status-badge status-${userDetails.status}`}>
                  {userDetails.status}
                </span>
              </div>
              <div className="info-item">
                <label>Investor Status</label>
                <span className={`status-badge status-${userDetails.investor === 'enable' ? 'active' : 'inactive'}`}>
                  {userDetails.investor === 'enable' ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="info-item">
                <label>Joined Date</label>
                <span>{userDetails.joined}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="detail-card">
          <div className="card-header">
            <h2><FaMoneyBillWave /> Financial Information</h2>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Deposit Amount</label>
                <span className="amount-value">${Number(userDetails.deposit).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <label>Total Income</label>
                <span className="amount-value success">${Number(userDetails.total_income).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <label>Available Income</label>
                <span className="amount-value primary">${Number(userDetails.available_income).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <label>Withdrawn Amount</label>
                <span className="amount-value warning">${Number(userDetails.withdraw_income).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="detail-card">
          <div className="card-header">
            <h2><FaChartLine /> Business Information</h2>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Direct Business</label>
                <span className="amount-value">${Number(userDetails.direct_business).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <label>Team Business</label>
                <span className="amount-value">${Number(userDetails.team_business).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <label>Direct Referrals</label>
                <span className="count-value">{userDetails.direct_referral}</span>
              </div>
              <div className="info-item">
                <label>Direct Active</label>
                <span className="count-value">{userDetails.direct_active}</span>
              </div>
              <div className="info-item">
                <label>Total Team Members</label>
                <span className="count-value">{userDetails.total_team}</span>
              </div>
              <div className="info-item">
                <label>Total Active Members</label>
                <span className="count-value">{userDetails.total_active}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Income Breakdown */}
        <div className="detail-card">
          <div className="card-header">
            <h2><FaTrophy /> Income Breakdown</h2>
          </div>
          <div className="card-body">
            <div className="income-grid">
              <div className="income-item">
                <div className="income-label">
                  <span className="income-dot direct"></span>
                  Direct Income
                </div>
                <span className="income-value">${Number(userDetails.direct_income).toLocaleString()}</span>
              </div>
              <div className="income-item">
                <div className="income-label">
                  <span className="income-dot roi"></span>
                  ROI Income
                </div>
                <span className="income-value">${Number(userDetails.roi_income).toLocaleString()}</span>
              </div>
              <div className="income-item">
                <div className="income-label">
                  <span className="income-dot level"></span>
                  Level Income
                </div>
                <span className="income-value">${Number(userDetails.level_income).toLocaleString()}</span>
              </div>
              <div className="income-item">
                <div className="income-label">
                  <span className="income-dot salary"></span>
                  Salary Income
                </div>
                <span className="income-value">${Number(userDetails.salary_income).toLocaleString()}</span>
              </div>
              <div className="income-item">
                <div className="income-label">
                  <span className="income-dot reward"></span>
                  Reward Income
                </div>
                <span className="income-value">${Number(userDetails.reward_income).toLocaleString()}</span>
              </div>
              <div className="income-item">
                <div className="income-label">
                  <span className="income-dot smart"></span>
                  Smart Income
                </div>
                <span className="income-value">${Number(userDetails.smart_income).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Topup Modal */}
      <TopupModal
        isOpen={showTopupModal}
        onClose={() => setShowTopupModal(false)}
        userDetails={userDetails}
        onConfirm={handleTopup}
      />
    </div>
  );
};

export default UserDetails;